import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveMedia } from "../lib/api";
import { InkPaloma, InkOcote, InkSailboat, InkFlourish } from "../components/Ink";

const CATEGORIES = ["Todos", "Recién Abierto", "Esta Semana", "Para Reiniciar", "Gastronomía", "Guía de Temporada"];

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [active, setActive] = useState("Todos");
  const [loading, setLoading] = useState(true);
  const [heroIdx, setHeroIdx] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    api.get("/articles").then((r) => { setArticles(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const featuredList = articles.filter((a) => a.featured);
  const featured = featuredList[heroIdx] || featuredList[0] || articles[0];

  // Random starting hero on first mount (feels fresh on repeat visits)
  useEffect(() => {
    if (featuredList.length > 1) setHeroIdx(Math.floor(Math.random() * featuredList.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuredList.length]);

  // Auto-rotate every 7s with a short fade
  useEffect(() => {
    if (featuredList.length < 2) return;
    const t = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setHeroIdx((i) => (i + 1) % featuredList.length);
        setFading(false);
      }, 450);
    }, 7000);
    return () => clearInterval(t);
  }, [featuredList.length]);

  const goToHero = (idx) => {
    if (idx === heroIdx) return;
    setFading(true);
    setTimeout(() => { setHeroIdx(idx); setFading(false); }, 350);
  };

  const showFeatured = active === "Todos";
  const gridSource = active === "Todos" ? articles.filter((a) => a.id !== featured?.id) : articles;
  const filtered = active === "Todos" ? gridSource : gridSource.filter((a) => a.category === active);

  const today = new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div data-testid="home-page" className="bg-white relative">
      {/* Section side-rail (desktop) */}
      <aside className="hidden xl:flex fixed left-6 top-1/2 -translate-y-1/2 z-30 flex-col gap-4">
        <p className="eyebrow rotate-180" style={{ writingMode: "vertical-rl" }}>En Esta Edición</p>
        <div className="flex flex-col gap-3 mt-2">
          {[
            { id: "journal", label: "Journal" },
            { id: "lago", label: "Lago" },
            { id: "bosque", label: "Bosque" },
            { id: "despacho", label: "Despacho" },
          ].map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`anchor-${s.id}`}
              className="group flex items-center gap-3 text-[0.62rem] tracking-[0.28em] uppercase text-cobalt/60 hover:text-cobalt font-display font-semibold"
            >
              <span className="w-6 h-px bg-cobalt/40 group-hover:bg-cobalt group-hover:w-10 transition-all" />
              {s.label}
            </a>
          ))}
        </div>
      </aside>
      {/* Date strip */}
      <section>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-4 flex items-center justify-between text-[0.62rem] tracking-[0.32em] uppercase text-cobalt font-display font-semibold border-b border-black/10">
          <p>Edición · {today}</p>
          <p className="hidden sm:block">Valle de Bravo · 19° · Bruma alta</p>
          <p>Vol. XI</p>
        </div>
      </section>

      {/* ─────── FEATURED LEAD — Full-bleed hero image (rotating) ─────── */}
      {featured && showFeatured && (
        <section className="relative overflow-hidden">
          <Link to={`/journal/${featured.id}`} className="group block relative" data-testid="featured-article">
            <div className="relative w-full h-[78vh] min-h-[560px] max-h-[880px] overflow-hidden bg-cobalt-deep">
              {featuredList.map((a, i) => (
                a.hero_video ? (
                  <video
                    key={a.id}
                    src={resolveMedia(a.hero_video)}
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={resolveMedia(a.image)}
                    className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-in-out"
                    style={{ opacity: i === heroIdx ? 1 : 0 }}
                    aria-hidden={i !== heroIdx}
                  />
                ) : (
                  <img
                    key={a.id}
                    src={resolveMedia(a.image)}
                    alt={a.title}
                    className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-in-out"
                    style={{ opacity: i === heroIdx ? 1 : 0 }}
                  />
                )
              ))}
              {/* Readability vignette */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" aria-hidden="true" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/10 to-transparent" aria-hidden="true" />
              <InkPaloma className="hidden lg:block absolute top-10 right-16 w-40 h-24 opacity-80" stroke="#E88A5A" />

              {/* Overlay text — fades on hero change */}
              <div className={`absolute inset-0 flex items-end transition-opacity duration-500 ${fading ? "opacity-0" : "opacity-100"}`}>
                <div className="max-w-7xl mx-auto w-full px-6 sm:px-10 lg:px-16 pb-16 sm:pb-20 lg:pb-24">
                  <div className="max-w-2xl text-white">
                    <p className="eyebrow-on-dark">La Portada · N° 11</p>
                    <p className="script-cream text-3xl sm:text-4xl mt-3" style={{ color: "#E88A5A" }}>{featured.category.toLowerCase()}</p>
                    <h1 className="mt-4 font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.0] drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]">
                      {featured.title}
                    </h1>
                    <p className="mt-6 text-[1.02rem] sm:text-lg text-white/90 leading-relaxed max-w-xl">{featured.dek}</p>
                    <div className="mt-6 flex items-center gap-4 text-[0.62rem] tracking-[0.28em] uppercase text-white/75 font-display font-semibold">
                      <span>Por {featured.author}</span>
                      <span className="w-6 h-px bg-white/50" />
                      <span>Lectura de 4 min</span>
                    </div>
                    <span className="btn-terracotta mt-8">Leer la nota</span>
                  </div>
                </div>
              </div>

              {/* Dot indicators */}
              {featuredList.length > 1 && (
                <div className="absolute bottom-6 sm:bottom-8 right-6 sm:right-10 lg:right-16 flex items-center gap-3 z-10">
                  {featuredList.map((_, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToHero(i); }}
                      data-testid={`hero-dot-${i}`}
                      aria-label={`Ir a portada ${i + 1}`}
                      className={`transition-all duration-300 ${i === heroIdx ? "w-8 h-[2px] bg-white" : "w-4 h-[2px] bg-white/40 hover:bg-white/70"}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </Link>
        </section>
      )}

      {/* Category filter — white */}
      <section className="bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-5 flex items-center gap-6 sm:gap-10 overflow-x-auto">
          <p className="eyebrow shrink-0">Explorar</p>
          <div className="w-px h-4 bg-black/20 shrink-0" />
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`category-${c}`}
              className={`font-display text-[0.68rem] tracking-[0.24em] uppercase whitespace-nowrap font-semibold transition-colors ${
                active === c ? "text-cobalt" : "text-ink/55 hover:text-cobalt"
              }`}
            >
              {c}
              {active === c && <span className="block h-px bg-cobalt mt-1.5" />}
            </button>
          ))}
        </div>
      </section>

      {/* ─────── EDITION TITLE — white ─────── */}
      <section id="journal" className="bg-white max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-20 pb-4 scroll-mt-32">
        <div className="flex items-end justify-between gap-8 flex-wrap">
          <div>
            <p className="eyebrow">La Edición · N° 11</p>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-ink max-w-2xl leading-tight">Lo que llegó <span className="serif-italic text-cobalt">esta semana</span> al Valle.</h2>
          </div>
          <InkOcote className="hidden md:block w-14 h-28 opacity-80" />
        </div>
        <div className="mt-8 rule-hair" />
      </section>

      {/* ─────── ARTICLE GRID — white ─────── */}
      <section className="bg-white max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-10 pb-24">
        {loading ? (
          <p className="text-subtle text-sm">Cargando la edición…</p>
        ) : filtered.length === 0 ? (
          <p className="text-subtle text-sm">Sin notas en esta categoría por ahora.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
            {filtered.map((a, i) => (
              <Link
                to={`/journal/${a.id}`}
                key={a.id}
                className="group card-hover fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`article-card-${a.id}`}
              >
                <div className="overflow-hidden aspect-[4/5]">
                  <img src={resolveMedia(a.image)} alt={a.title} className="card-img w-full h-full object-cover" />
                </div>
                <div className="mt-5">
                  <p className="eyebrow">{a.category}</p>
                  <h3 className="mt-3 font-serif text-2xl leading-snug text-ink group-hover:text-cobalt transition-colors">{a.title}</h3>
                  <p className="mt-3 text-[0.92rem] text-muted2 leading-relaxed line-clamp-2">{a.dek}</p>
                  <p className="mt-4 text-[0.6rem] font-display font-semibold tracking-[0.28em] uppercase text-subtle">Por {a.author} · {i + 1} min</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ─────── LAKE / FOREST — Sand canvas ─────── */}
      <section className="canvas-sand py-24 scroll-mt-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid gap-16 md:grid-cols-2">
          <div id="lago" className="flex items-start gap-8 scroll-mt-32">
            <InkSailboat className="w-28 h-28 shrink-0 mt-1" stroke="#1F3A6E" />
            <div>
              <p className="script text-4xl leading-none">en el lago</p>
              <p className="mt-3 font-serif text-xl text-cobalt-deep leading-snug max-w-sm">Regatas, brunches en muelle, botes de madera restaurados. Lo que se mueve sobre el agua este mes.</p>
              <Link to="/calendario" className="mt-4 inline-block text-[0.68rem] tracking-[0.26em] uppercase text-cobalt border-b border-cobalt pb-1 font-display font-semibold">Ver eventos del lago</Link>
            </div>
          </div>
          <div id="bosque" className="flex items-start gap-8 scroll-mt-32">
            <InkOcote className="w-20 h-32 shrink-0 mt-1" stroke="#1F3A6E" />
            <div>
              <p className="script text-4xl leading-none">en el bosque</p>
              <p className="mt-3 font-serif text-xl text-cobalt-deep leading-snug max-w-sm">Cenas privadas bajo los ocotes, temazcal, panaderías con horno de leña. La otra mitad del fin de semana.</p>
              <Link to="/experiencias" className="mt-4 inline-block text-[0.68rem] tracking-[0.26em] uppercase text-cobalt border-b border-cobalt pb-1 font-display font-semibold">Ver experiencias</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─────── QUOTE PULL — white ─────── */}
      <section className="bg-white max-w-4xl mx-auto px-6 sm:px-10 py-24 text-center">
        <InkFlourish className="w-32 h-4 mx-auto opacity-60" />
        <blockquote className="mt-8 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-ink">
          Nada de <span className="serif-italic text-cobalt">itinerarios.</span> Solo lo que vale su fin de semana.
        </blockquote>
        <p className="mt-8 text-[0.68rem] tracking-[0.28em] uppercase text-cobalt font-display font-semibold">— La Redacción</p>
      </section>

      {/* ─────── NEWSLETTER — Rust canvas ─────── */}
      <section id="despacho" className="canvas-rust py-20 sm:py-24 relative overflow-hidden scroll-mt-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid gap-12 md:grid-cols-12 items-center">
          <div className="md:col-span-5">
            <p className="script-cream text-5xl sm:text-6xl leading-none">el despacho</p>
            <p className="eyebrow-on-dark mt-4">Del Fin de Semana</p>
            <InkFlourish className="w-36 h-5 mt-6 opacity-80" stroke="#FFFFFF" />
          </div>
          <div className="md:col-span-7 text-white">
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05]">Reciba la edición cada <span className="serif-italic">jueves</span> por la mañana.</h2>
            <p className="mt-5 text-white/85 leading-relaxed max-w-xl">Una nota corta con lo que abre, lo que vale la pena y lo que dejamos para otro fin. Nada más.</p>
            <form className="mt-8 flex flex-col sm:flex-row gap-4 max-w-lg" onSubmit={async (e) => {
              e.preventDefault();
              const email = e.target.email.value;
              const btn = e.target.querySelector('button');
              const original = btn.textContent;
              btn.disabled = true;
              btn.textContent = "Enviando…";
              try {
                await api.post("/subscribers", { email });
                btn.textContent = "¡Suscrito!";
                e.target.reset();
                setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 2800);
              } catch (err) {
                btn.textContent = "Error — intente de nuevo";
                setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 2800);
              }
            }}>
              <input
                type="email"
                name="email"
                required
                placeholder="Su correo"
                data-testid="newsletter-email"
                className="flex-1 bg-transparent border-b border-white text-white placeholder-white/60 py-3 focus:border-white"
              />
              <button data-testid="newsletter-submit" className="btn-outline-cream">Suscribirme</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
